import React, { useEffect, useState } from "react";
import { useAuthState } from "../provider/AuthProvider";
import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import { Button, Col, Divider, Input, Modal, Row, Space } from "antd";

import * as Request from "../commons/request";
import * as Types from "../commons/typeDTO";
import * as Strings from "../commons/string";
import * as Common from "../commons/common";
import * as Utils from "../utils/utils";

import styles from "../pages/RentInfo.module.css";
import ModalAddRentPayment from "./ModalAddRentPayment";
import ModalUpdateRentPayment from "./ModalUpdateRentPayment";
import moment from "moment";

export type RentPaymentsByPaymentCategory = {
    [key: number]: Array<Types.RentPaymentDto>;
};

const PAID_ITEM_ID = -1;

function RentPaymentDetail({ rent, onUpdateRent }: { rent: Types.RentDto; onUpdateRent?: () => void }) {
    const userDetails = useAuthState();

    const [rentCategoryPayment, setRentCategoryPayment] = useState<RentPaymentsByPaymentCategory>({});
    const [totalUnpaidAmount, setTotalUnpaidAmount] = useState(0);

    const [openModalUpdateRentPayment, setOpenModalUpdateRentPayment] = useState(false);
    const [updateRentPayment, setUpdateRentPayment] = useState<Types.RentPaymentDto>();

    const [penaltyList, setPenaltyList] = useState<Array<Types.RentPaymentDto>>([]);
    const [paidRentPayments, setPaidRentPayments] = useState<Array<Types.RentPaymentDto>>([]);
    const [paidDate, setPaidDate] = useState<string>();

    const [etcPayments, setEtcPayments] = useState<Array<Types.RentPaymentDto>>([]);
    const [etcPaymentName, setEtcPaymentName] = useState<string>();
    const [etcPaymentAmount, setEtcPaymentAmount] = useState<number>();

    useEffect(() => {
        Utils.onEvent(Common.EVENT_CLOSE_ALL_MODAL, handleCloseAllModal);
        return () => {
            Utils.offEvent(Common.EVENT_CLOSE_ALL_MODAL, handleCloseAllModal);
        };
    }, []);

    const handleCloseAllModal = () => {
        Modal.destroyAll();
        setOpenModalUpdateRentPayment(false);
    };

    useEffect(() => {
        const etcPaymentList = rent.rentPayments?.filter((payment) => payment.memo?.includes("[기타]"));
        setEtcPayments(etcPaymentList);

        const tempPenaltyList = rent.rentPayments?.filter(
            (payment) => payment.rentPaymentType === "PENALTY" || payment.rentPaymentType === "PAID_ITEM" || payment.rentPaymentType === "INSURANCE"
        );

        setPenaltyList(tempPenaltyList);
        setPaidRentPayments(
            rent.rentPayments?.filter(
                (payment) =>
                    (payment.rentPaymentType === "PENALTY" || payment.rentPaymentType === "PAID_ITEM" || payment.rentPaymentType === "INSURANCE") &&
                    payment.paidDate === null
            )
        );
        setPaidDate(moment().format(Common.FORMAT_DATE_TIME));

        let total = 0;
        const tempObject: RentPaymentsByPaymentCategory = {};
        tempPenaltyList?.forEach((payment) => {
            total += payment.amount;
            if (payment?.memo?.includes("[기타]")) return;
            if (payment.rentPaymentItem) {
                if (!tempObject[payment.rentPaymentItem.rentPaymentCategory.rentPaymentCategoryId]) {
                    tempObject[payment.rentPaymentItem.rentPaymentCategory.rentPaymentCategoryId] = [];
                }
                tempObject[payment.rentPaymentItem.rentPaymentCategory.rentPaymentCategoryId].push(payment);
            } else {
                tempObject[PAID_ITEM_ID] = [];
                tempObject[PAID_ITEM_ID].push(payment);
            }
        });

        setTotalUnpaidAmount(total);
        setRentCategoryPayment(tempObject);
    }, [rent]);

    const requestAxiosAddPayment = async (payment: any) => {
        const response = await utilAxiosWithAuth().post(Request.RENT_PAYMENT_ADD_URL, payment);
        return response.data;
    };

    const {
        loading: loadingAddPayment,
        error: errorAddPayment,
        data: resultAddPayment,
        execute: requestAddPayment,
    } = useAsyncAxios(requestAxiosAddPayment);

    useEffect(() => {
        if (resultAddPayment === null) return;
        console.log("resultAddPayment", resultAddPayment);
        if (resultAddPayment.payment?.memo?.includes("[메모]")) {
            setEtcPayments([...etcPayments, resultAddPayment.payment]);
        }
        setEtcPaymentName(undefined);
        setEtcPaymentAmount(undefined);
        onUpdateRent && onUpdateRent();
    }, [resultAddPayment]);

    useEffect(() => {
        if (errorAddPayment === null) return;
        console.log("errorAddPayment", errorAddPayment);
    }, [errorAddPayment]);

    const requestAxiosUpdatePayment = async (payment: any) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_PAYMENT_URL + payment.rentPaymentId, payment);
        return response.data;
    };

    const {
        loading: loadingUpdatePayment,
        error: errorUpdatePayment,
        data: resultUpdatePayment,
        execute: requestUpdatePayment,
    } = useAsyncAxios(requestAxiosUpdatePayment);

    useEffect(() => {
        if (resultUpdatePayment === null) return;
        console.log("resultUpdatePayment", resultUpdatePayment);
        onUpdateRent && onUpdateRent();
    }, [resultUpdatePayment]);

    useEffect(() => {
        if (errorUpdatePayment === null) return;
        console.log("errorUpdatePayment", errorUpdatePayment);
    }, [errorUpdatePayment]);

    const onDeletePayment = (rentPayment: Types.RentPaymentDto) => {
        showDeleteConfirm(rentPayment);
    };

    const showDeleteConfirm = (rentPayment: Types.RentPaymentDto) => {
        Utils.addModalStack();
        Modal.confirm({
            title: "등록 삭제",
            content: "삭제하시겠습니까?",
            okText: "확인",
            onOk() {
                Utils.popModalStack();
                setOpenModalUpdateRentPayment(false);
                requestDeletePayment(rentPayment.rentPaymentId);
                if (rentPayment.rentPaymentType === "PAID_ITEM") {
                    requestDeletePaidItem(rent.rentId, rentPayment.rentPaymentItem.rentPaymentItemName);
                }
            },
            cancelText: "취소",
            onCancel() {
                Utils.popModalStack();
            },
            centered: true,
        });
    };

    const requestAxiosDeletePaidItem = async (rentId: number, rentPaidItemName: string) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_URL + rentId, {
            rentPaidItems: rent.rentPaidItems?.filter((item) => item.rentPaidItemName !== rentPaidItemName),
        });
        return response.data;
    };

    const {
        loading: loadingDeletePaidItem,
        error: errorDeletePaidItem,
        data: resultDeletePaidItem,
        execute: requestDeletePaidItem,
    } = useAsyncAxios(requestAxiosDeletePaidItem);

    useEffect(() => {
        if (resultDeletePaidItem === null) return;
        console.log("resultDeletePaidItem", resultDeletePaidItem);
        onUpdateRent && onUpdateRent();
    }, [resultDeletePaidItem]);

    useEffect(() => {
        if (errorDeletePaidItem === null) return;
        console.log("errorDeletePaidItem", errorDeletePaidItem);
    }, [errorDeletePaidItem]);

    const requestAxiosDeletePayment = async (rentPaymentId: number) => {
        const response = await utilAxiosWithAuth().delete(Request.RENT_PAYMENT_URL + rentPaymentId);
        return response.data;
    };

    const {
        loading: loadingDeletePayment,
        error: errorDeletePayment,
        data: resultDeletePayment,
        execute: requestDeletePayment,
    } = useAsyncAxios(requestAxiosDeletePayment);

    useEffect(() => {
        if (resultDeletePayment === null) return;
        console.log("resultDeletePayment", resultDeletePayment);
        onUpdateRent && onUpdateRent();
    }, [resultDeletePayment]);

    useEffect(() => {
        if (errorDeletePayment === null) return;
        console.log("errorDeletePayment", errorDeletePayment);
    }, [errorDeletePayment]);

    const onSetPaidDate = (item: Types.RentPaymentDto) => {
        Modal.confirm({
            title: "정산",
            content: "정산 완료 하시겠습니까?",
            okText: "확인",
            onOk() {
                saveUpdatePaymentPaidDate(item);
            },
            cancelText: "취소",
            onCancel() {},
            centered: true,
        });
    };

    const onSetUnPaidDate = (item: Types.RentPaymentDto) => {
        Modal.confirm({
            title: "정산 취소",
            content: "정산 취소 하시겠습니까?",
            okText: "확인",
            onOk() {
                deletePaymentPaidDate(item);
            },
            cancelText: "취소",
            onCancel() {},
            centered: true,
        });
    };

    const onSetAllPaidDate = () => {
        Modal.confirm({
            title: "일괄 정산",
            content: "모든 결제가 정산되었습니까?",
            okText: "정산완료",
            onOk() {
                saveUpdatePaymentPaidDate();
            },
            cancelText: "취소",
            onCancel() {},
            centered: true,
        });
    };

    const saveUpdatePaymentPaidDate = (item?: Types.RentPaymentDto) => {
        if (item) {
            return requestUpdateDatePayment(item, paidDate);
        } else {
            paidRentPayments.map((pay) => {
                requestUpdateDatePayment(pay, paidDate);
            });
        }
    };

    const deletePaymentPaidDate = (item: Types.RentPaymentDto) => {
        if (item) {
            requestUpdateDatePayment(item, undefined);
        }
    };

    const requestAxiosUpdateDatePayment = async (rentPayment: Types.RentPaymentDto, paidDate?: string) => {
        const payment: any = {
            paidDate: paidDate,
        };

        const response = await utilAxiosWithAuth().put(Request.RENT_PAYMENT_URL + rentPayment.rentPaymentId, payment);
        return response.data;
    };

    const {
        loading: loadingUpdateDatePayment,
        error: errorUpdateDatePayment,
        data: resultUpdateDatePayment,
        execute: requestUpdateDatePayment,
    } = useAsyncAxios(requestAxiosUpdateDatePayment);

    useEffect(() => {
        if (resultUpdateDatePayment === null) return;
        console.log("resultUpdateDatePayment", resultUpdateDatePayment);
        onUpdateRent && onUpdateRent();
    }, [resultUpdateDatePayment]);

    useEffect(() => {
        if (errorUpdateDatePayment === null) return;
        console.log("errorUpdateDatePayment", errorUpdateDatePayment);
    }, [errorUpdateDatePayment]);

    const displayPaymentSummary = () => {
        if (penaltyList && penaltyList.length > 0) {
            const unPaidArray: number[] = [];
            const paidArray: number[] = [];

            penaltyList.map((penalty) => {
                if (penalty.paidDate === null) {
                    unPaidArray.push(penalty.amount);
                } else {
                    paidArray.push(penalty.amount);
                }
            });

            return (
                <>
                    <Row align="middle" justify="space-between">
                        <span>미정산</span>
                        <span style={{ color: "var(--danger-color)" }}>
                            {unPaidArray.length > 0 ? Utils.convertCurrencyKorean(unPaidArray.reduce((a, b) => a + b)) : "0원"}
                        </span>
                    </Row>
                    <Row align="middle" justify="space-between">
                        <span>정산 완료</span>
                        <span>{paidArray.length > 0 ? Utils.convertCurrencyKorean(paidArray.reduce((a, b) => a + b)) : "0원"}</span>
                    </Row>
                    <Divider className={styles.subTitle} />
                </>
            );
        }

        return null;
    };

    return (
        <Space direction="vertical" size={24} style={{ width: "100%" }}>
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                <Divider className={styles.subTitle} orientation="left">
                    <span>정산 정보</span>
                </Divider>

                {Object.keys(rentCategoryPayment).map((key, index) => {
                    const categoryPaymentList = rentCategoryPayment[Number(key)];
                    if (categoryPaymentList.length === 0) return null;

                    let paymentKeyValue;
                    let paymentCategoryName;
                    if (Number(key) === PAID_ITEM_ID) {
                        paymentKeyValue = key;
                        paymentCategoryName = "유료 대여 품목";
                    } else {
                        const category = categoryPaymentList[0].rentPaymentItem.rentPaymentCategory;
                        paymentKeyValue = category.rentPaymentCategoryId;
                        paymentCategoryName = category.rentPaymentCategoryName;
                    }
                    // const category = categoryPaymentList[0].rentPaymentItem.rentPaymentCategory;
                    return (
                        <div key={paymentKeyValue}>
                            <Col span={24}>
                                <h4 style={{ marginTop: 0, marginBottom: 0 }}>{paymentCategoryName}</h4>
                            </Col>
                            {categoryPaymentList.map((item) => {
                                let paymentName;
                                if (Number(key) === PAID_ITEM_ID) {
                                    paymentName = rent?.rentPaidItems?.length > 0 && rent.rentPaidItems[0].rentPaidItemName;
                                } else {
                                    paymentName = item.rentPaymentItem.rentPaymentItemName;
                                }
                                return (
                                    <Row key={item.rentPaymentId} align="middle" justify="space-between" style={{ marginBottom: "10px" }}>
                                        <p style={{ color: item.paidDate ? "black" : "var(--danger-color)" }}>
                                            {item.paidDate ? "[완]" : "[미]"} {paymentName}
                                        </p>
                                        <Space size={5}>
                                            <div
                                                className={styles.input}
                                                style={{ background: item.paidDate ? "white" : "" }}
                                                onClick={() => {
                                                    if (!item.paidDate) {
                                                        setUpdateRentPayment(item);
                                                        setOpenModalUpdateRentPayment(true);
                                                    }
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        margin: item.paidDate ? "4px 0" : "",
                                                        color: item.paidDate ? "black" : "var(--danger-color)",
                                                    }}
                                                >
                                                    {item.amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                                </span>
                                            </div>
                                            <span>원</span>
                                            <Button
                                                disabled={!item.amount}
                                                onClick={() => {
                                                    item.paidDate ? onSetUnPaidDate(item) : onSetPaidDate(item);
                                                }}
                                            >
                                                {item.paidDate ? "취소" : "정산"}
                                            </Button>
                                        </Space>
                                    </Row>
                                );
                            })}
                            <Divider className={styles.subTitle} />
                        </div>
                    );
                })}
                <div>
                    <Col span={24}>
                        <h4 style={{ marginTop: 0, marginBottom: 0 }}>기타</h4>
                    </Col>
                    {etcPayments.map((pay) => {
                        return (
                            <Row key={pay?.rentPaymentId} align="middle" gutter={8} style={{ marginBottom: "10px" }}>
                                <Col span={12}>
                                    <div
                                        className={styles.input}
                                        style={{ background: pay.paidDate ? "white" : "", justifyContent: "left" }}
                                        onClick={() => {
                                            if (!pay.paidDate) {
                                                setUpdateRentPayment(pay);
                                                setOpenModalUpdateRentPayment(true);
                                            }
                                        }}
                                    >
                                        <span style={{ margin: pay.paidDate ? "4px 0" : "", color: pay.paidDate ? "black" : "var(--danger-color)" }}>
                                            {(pay.paidDate ? "[완] " : "[미] ") + pay?.memo?.substring(4)}
                                        </span>
                                    </div>
                                </Col>
                                <Col span={12}>
                                    <Space style={{ width: "100%", justifyContent: "right" }} size={5}>
                                        <div
                                            className={styles.input}
                                            style={{ background: pay.paidDate ? "white" : "" }}
                                            onClick={() => {
                                                if (!pay.paidDate) {
                                                    setUpdateRentPayment(pay);
                                                    setOpenModalUpdateRentPayment(true);
                                                }
                                            }}
                                        >
                                            <span
                                                style={{ margin: pay.paidDate ? "4px 0" : "", color: pay.paidDate ? "black" : "var(--danger-color)" }}
                                            >
                                                {pay?.amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                            </span>
                                        </div>
                                        <span>원</span>
                                        <Button
                                            disabled={!pay.amount}
                                            onClick={() => {
                                                pay.paidDate ? onSetUnPaidDate(pay) : onSetPaidDate(pay);
                                            }}
                                        >
                                            {pay.paidDate ? "취소" : "정산"}
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        );
                    })}
                    <Row align="middle" gutter={8} style={{ marginBottom: "10px" }}>
                        <Col span={12}>
                            <Input
                                className={styles.input}
                                placeholder="직접 입력"
                                value={etcPaymentName}
                                onChange={(value) => {
                                    setEtcPaymentName(value.target.value);
                                }}
                            />
                        </Col>
                        <Col span={12}>
                            <Space style={{ width: "100%", justifyContent: "right" }} size={5}>
                                <Input
                                    className={styles.input}
                                    style={{ width: 70, textAlign: "right" }}
                                    placeholder="0"
                                    value={etcPaymentAmount}
                                    onChange={(value) => {
                                        setEtcPaymentAmount(Number(value.target.value));
                                    }}
                                />
                                <span>원</span>
                                <Button
                                    disabled={!etcPaymentAmount || !etcPaymentName}
                                    onClick={() => {
                                        const payment: any = {
                                            rent: rent,
                                            rentPaymentType: "PENALTY",
                                            basicPrice: etcPaymentAmount,
                                            paymentMethod: "OFFLINE_BANK",
                                            amount: etcPaymentAmount,
                                            memo: "[기타]" + etcPaymentName,
                                        };
                                        requestAddPayment(payment);
                                    }}
                                >
                                    등록
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                    <Divider className={styles.subTitle} />
                </div>

                {displayPaymentSummary()}
                <Row align="middle" justify="space-between">
                    <h3 style={{ marginTop: 0 }}>총 합계</h3> <h3 style={{ marginTop: 0 }}>{Utils.convertCurrencyKorean(totalUnpaidAmount)}</h3>
                </Row>

                <Row gutter={10} style={{ width: "100%", margin: 0 }}>
                    <Col xs={24} className="gutter-row">
                        <Button
                            type="primary"
                            className="b-xs"
                            style={{ width: "100%" }}
                            disabled={paidRentPayments.length === 0}
                            onClick={onSetAllPaidDate}
                        >
                            일괄정산
                        </Button>
                    </Col>
                </Row>

                {updateRentPayment && (
                    <ModalUpdateRentPayment
                        rent={rent}
                        rentPayment={updateRentPayment}
                        open={openModalUpdateRentPayment}
                        onChangedOpen={(open: boolean) => {
                            setOpenModalUpdateRentPayment(open);
                            setUpdateRentPayment(undefined);
                        }}
                        onChangedPayment={(value: any) => {
                            requestUpdatePayment(value);
                            setOpenModalUpdateRentPayment(false);
                        }}
                        onDeletedPayment={(payment: Types.RentPaymentDto) => {
                            onDeletePayment(payment);
                        }}
                    />
                )}
            </Space>
        </Space>
    );
}

export default RentPaymentDetail;
